import { request } from "$/lib/api"
import {
  BookingStepOrder,
  CreateBookingStepOrder,
  UpdateBookingStepOrder,
} from "@repo/common"
import { Route, populateRoute } from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

interface BookingStepOrderWithDetails extends BookingStepOrder {
  customSection?: {
    id: string
    name: string
    items: Array<{
      id: string
      name: string
      description: string | null
      price: number
      image: string | null
    }>
  } | null
}

const bookingStepOrderQueries = {
  list: ({ storeId }: { storeId: string }) =>
    queryOptions({
      queryKey: ["booking-step-orders", storeId],
      queryFn: async () => {
        return request<BookingStepOrderWithDetails[]>({
          method: "GET",
          url: populateRoute(
            Route.BOOKING_STEP_ORDER_CONTROLLER__FIND_MANY_BY_STORE_ID,
            storeId,
          ),
        })
      },
    }),
}

export function useBookingStepOrderMutations(storeId: string) {
  const queryClient = useQueryClient()

  const upsertManyMutation = useMutation({
    mutationFn: async (orders: CreateBookingStepOrder[]) => {
      return request<BookingStepOrderWithDetails[]>({
        method: "PUT",
        url: populateRoute(
          Route.BOOKING_STEP_ORDER_CONTROLLER__UPSERT_MANY,
          storeId,
        ),
        data: orders,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["booking-step-orders", storeId],
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: UpdateBookingStepOrder
    }) => {
      return request<BookingStepOrderWithDetails>({
        method: "PATCH",
        url: populateRoute(Route.BOOKING_STEP_ORDER_CONTROLLER__UPDATE, id),
        data,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["booking-step-orders", storeId],
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return request<void>({
        method: "DELETE",
        url: populateRoute(Route.BOOKING_STEP_ORDER_CONTROLLER__DELETE, id),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["booking-step-orders", storeId],
      })
    },
  })

  return {
    upsertManyMutation,
    updateMutation,
    deleteMutation,
  }
}

export default bookingStepOrderQueries
